export interface SessionStatus {
  authenticated: boolean;
}

export interface LoginRequest {
  password: string;
}

export interface ApiErrorBody {
  error: { code: string; message: string };
}

export interface Exercise {
  id: number;
  name: string;
  muscle_group: string | null;
  notes: string | null;
  archived: boolean;
}

export interface NewExercise {
  name: string;
  muscle_group?: string | null;
  notes?: string | null;
}

export interface Program {
  id: number;
  name: string;
  notes: string | null;
  created_at: string;
  archived: boolean;
  is_active: boolean;
}

export interface NewProgram {
  name: string;
  notes?: string | null;
}

export interface TrainingDay {
  id: number;
  program_id: number;
  name: string;
  position: number;
}

export interface NewTrainingDay {
  program_id: number;
  name: string;
}

export interface DayExercise {
  id: number;
  training_day_id: number;
  exercise_id: number;
  position: number;
  target_sets: number | null;
  target_reps: number | null;
  target_weight_kg: number | null;
  exercise_name: string;
  exercise_muscle_group: string | null;
  exercise_archived: boolean;
}

export interface NewDayExercise {
  training_day_id: number;
  exercise_id: number;
  target_sets?: number | null;
  target_reps?: number | null;
  target_weight_kg?: number | null;
}

export interface DayExercisePatch {
  target_sets?: number | null;
  target_reps?: number | null;
  target_weight_kg?: number | null;
}

export interface Workout {
  id: number;
  training_day_id: number | null;
  started_at: string;
  ended_at: string | null;
  notes: string | null;
}

export interface NewWorkout {
  training_day_id?: number | null;
  notes?: string | null;
}

export interface WorkoutPatch {
  notes?: string | null;
}

export interface WorkoutListItem extends Workout {
  training_day_name: string | null;
  set_count: number;
}

export interface WorkoutSet {
  id: number;
  workout_id: number;
  exercise_id: number;
  set_index: number;
  weight_kg: number | null;
  reps: number | null;
  completed_at: string | null;
}

export interface NewWorkoutSet {
  exercise_id: number;
  weight_kg: number;
  reps: number;
}

export interface WorkoutSetPatch {
  weight_kg?: number;
  reps?: number;
}

export interface WorkoutExercise {
  exercise_id: number;
  exercise_name: string;
  exercise_muscle_group: string | null;
  position: number;
  source: "planned" | "ad-hoc";
}

export interface BumpDelta<T> {
  before: T | null;
  after: T;
}

export interface PrescriptionBump {
  day_exercise_id: number;
  exercise_id: number;
  exercise_name: string;
  sets: BumpDelta<number> | null;
  reps: BumpDelta<number> | null;
  weight_kg: BumpDelta<number> | null;
}

export interface FinishWorkoutResult {
  workout: Workout;
  bumps: PrescriptionBump[];
}

export interface FinishWorkoutOptions {
  started_at?: string;
  ended_at?: string;
}

export type QueuedMutation =
  | {
      kind: "addWorkoutSet";
      workout_id: number;
      input: { exercise_id: number; weight_kg: number; reps: number };
      temp_id: string;
      created_at: string;
    }
  | {
      kind: "updateWorkoutSet";
      set_id: number;
      patch: WorkoutSetPatch;
      created_at: string;
    }
  | {
      kind: "deleteWorkoutSet";
      set_id: number;
      created_at: string;
    }
  | {
      kind: "finishWorkout";
      workout_id: number;
      opts?: FinishWorkoutOptions;
      created_at: string;
    };

export type QueuedMutationKind = QueuedMutation["kind"];

export { epley1rm } from "./formulas";

export interface ProgressionPoint {
  workout_id: number;
  finished_at: string;
  best_set: {
    weight_kg: number;
    reps: number;
    e1rm: number;
  };
  /** Σ weight_kg × reps over every set of the exercise in that workout. */
  volume: number;
}

export type RepBucketKey = "1" | "2-5" | "6-8" | "9-12" | "13+";

export interface RepBucket {
  key: RepBucketKey;
  label: string;
  min: number;
  max: number;
}

export const REP_BUCKETS: readonly RepBucket[] = [
  { key: "1", label: "1 rep", min: 1, max: 1 },
  { key: "2-5", label: "2–5 reps", min: 2, max: 5 },
  { key: "6-8", label: "6–8 reps", min: 6, max: 8 },
  { key: "9-12", label: "9–12 reps", min: 9, max: 12 },
  { key: "13+", label: "13+ reps", min: 13, max: Number.POSITIVE_INFINITY },
] as const;

export function repBucket(reps: number): RepBucketKey {
  if (!Number.isInteger(reps) || reps < 1) {
    throw new RangeError("reps must be an integer >= 1");
  }
  for (const b of REP_BUCKETS) {
    if (reps >= b.min && reps <= b.max) return b.key;
  }
  return "13+";
}

export interface PersonalRecord {
  bucket: RepBucketKey;
  weight_kg: number;
  reps: number;
  e1rm: number;
  workout_id: number;
  finished_at: string;
}

export interface BodyMetric {
  id: number;
  measured_at: string;
  body_weight_kg: number | null;
  notes: string | null;
}

export interface NewBodyMetric {
  body_weight_kg: number;
  measured_at?: string;
  notes?: string | null;
}

export type PeriodKind = "weekly" | "monthly";

export interface PeriodSummary {
  period_kind: PeriodKind;
  period_start: string;
  sessions: number;
  total_volume_kg: number;
  total_sets: number;
  unique_exercises: number;
  top_e1rm_kg: number;
}

export interface MuscleGroupVolume {
  period_kind: PeriodKind;
  period_start: string;
  muscle_group: string;
  total_volume_kg: number;
  sets: number;
}

export interface PrCountPoint {
  period_kind: PeriodKind;
  period_start: string;
  count: number;
}

export interface ExerciseTotals {
  sets: number;
  total_volume_kg: number;
  top_set: {
    weight_kg: number;
    reps: number;
    e1rm: number;
  } | null;
}

export interface ExerciseComparison {
  exercise_id: number;
  exercise_name: string;
  current: ExerciseTotals;
  previous: {
    workout_id: number;
    finished_at: string;
    totals: ExerciseTotals;
  } | null;
}

export interface WorkoutComparison {
  workout_id: number;
  exercises: ExerciseComparison[];
}
